import { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useCookieState, useMemoizedFn } from 'ahooks';
import Cookies from 'js-cookie';
import { login } from '@dauyan/h5-tracking';
import { logError } from '@/utils/logger/arms-config';
import { QrcodeIcon, MobileIcon } from '@/components/common';
import { getTrustedRedirectUri, postAuthSuccessToParent } from '@/utils/redirect-uri';
import { externalLink } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { isIframeLoaded } from '@/utils/isIframe';
import i18n, { getLanguage } from '@/locales/init';
import bgImg from '@/assets/next_bg.jpeg';
import { checkToken, getUserInfo, organizationScopeFromLocation } from '@/apis/login';
import WechatLogin from './WeChatLogin';
import NormalLogin from './NormalLogin';
import './index.less';


const Login = () => {
  const [isLong, setIsLong] = useState(false);
  const [isWechatLogin, setWechatLogin] = useState(false);
  const [tosAgreed, settOsAgreed] = useState(false);
  const [tosAgreedVisible, setTosAgreedVisible] = useState(true);
  // wx 扫码登录后，如果 token 未返回则需要绑定手机号。
  const [wxId, setWxId] = useState('');
  const [wxTicket, setWxTicket] = useState('');

  const domain = useMemo(() => {
    if (window.location.href.includes('dauyan.net')) {
      return '.dauyan.net';
    }
    if (window.location.href.includes('dauyan.com')) {
      return '.dauyan.com';
    }
    return;
  }, []);

  const redirectURI = useMemo(() => getTrustedRedirectUri(), []);

  useEffect(() => {
    // 清理 org 页面的副作用，不清理不会影响业务
    Cookies.remove('_org');
  }, []);


  const [, setToken] = useCookieState('token', {
    domain: domain,
    path: '/',
    // 超时时间，如果 isLong 为 true，则为 30d-1h，否则为 7d-1h
    expires: new Date(Date.now() + (isLong ? 2592000000 - 3600000 : 604800000 - 3600000)),
  });
  const [, setOrg] = useCookieState('org', {
    domain: domain,
    path: '/',
  });
  const [, setOrganizationId] = useCookieState('organization_id', {
    domain: domain,
    path: '/',
  });

  const currentLang = useMemo(() => {
    return getLanguage();
  }, []);

  const links = useMemo(() => {
    return {
      copyright: externalLink?.copyright?.[currentLang],
      privacyPolicy: externalLink?.privacyPolicy?.[currentLang],
      disclaimer: externalLink?.disclaimer?.[currentLang],
      userAgreement: externalLink?.userAgreement?.[currentLang],
    };
  }, [currentLang]);

  const loginSuccess = useMemoizedFn((token: string, onlySetToken?: boolean) => {
    setOrg('');
    const organizationId = organizationScopeFromLocation();
    if (organizationId && organizationId !== '-1') {
      setOrganizationId(organizationId);
    } else {
      Cookies.remove('organization_id', {domain: domain, path: '/'});
    }
    if (token) {
      if (Cookies.get('token')) {
        Cookies.remove('token');
      }
      setToken(token);
    }

    getUserInfo().then((res) => {
      if (res.code === 200) {
        login(res.data.account);
      }
    }).catch((e) => {
      logError(e);
    }).finally(() => {

      if (onlySetToken) {
        return;
      }
      if (isIframeLoaded()) {
        if (!postAuthSuccessToParent(token, redirectURI)) {
          logError(new Error('Trusted redirect_uri is required for iframe login'));
        }
        return;
      }
      if (!redirectURI) {
        // eslint-disable-next-line i18n/no-chinese-character
        console.error('redirect_uri 不存在或不受信任');
        window.location.href = `https://ielts${domain}/`;
        return;
      }

      window.location.href = redirectURI;
    });

  });

  const notDevInWhiteList = () => {
    toast.error(i18n.t('common.dauyan_account_dev_whitelist'));
    setTimeout(() => {
      window.location.href = 'https://www.dauyan.com/';
    }, 3000);
  };

  useEffect(() => {
    if (!getToken()) {
      updateToken();
    }
  }, []);
  const updateToken = useMemoizedFn(() => {
    void checkToken()
      .then(res => {
        if (res?.data.token) {
          loginSuccess(res.data.token, true);
        }
      })
      // The login form remains usable when a local backend cannot create a
      // visitor session. Do not turn this optional session refresh into a page error.
      .catch(() => undefined);
  });


  function scanQrcode(token: string, studentId: number, wechatId: string, wechatTicket: string, is_bind_mobile: boolean) {
    if (is_bind_mobile && token) {
      loginSuccess(token);
      return;
    }

    toast.success(i18n.t('common.dauyan_account_login_bind_phone_no'));
    setWechatLogin(false);
    setWxId(wechatId);
    setWxTicket(wechatTicket);
  }


  const changeLoginType = () => {
    setWechatLogin(!isWechatLogin);
    // 如果是微信登录，则默认勾选同意协议。 如果是微信扫码成功后，需要绑定手机号，则不勾选同意协议。
    // settOsAgreed(isWechatLogin);
  };
  return (
    <div className="login-container w-full h-full max-[1000px]:justify-center">

      <div className="login-desc bg-red-200 flex-1 shrink-0 max-[1000px]:!hidden" style={{
        backgroundImage: `url(${bgImg})`
      }}>
        <div className="w-full h-full flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="desc-title-wrap">
            <div className="desc-title">{i18n.t('common.dauyan_account_des_title')}</div>
            <div className="desc-content">{i18n.t('common.dauyan_account_des_content')}</div>
          </div>
        </div>
      </div>

      <div className="login-main bg-blue-200">
        {
          !wxId && tosAgreedVisible && (
            <div className="change-login-type" onClick={changeLoginType}>
              {isWechatLogin ? <MobileIcon /> : <QrcodeIcon />}
              <div className="tips">{isWechatLogin ? i18n.t('common.dauyan_account_account_login') : i18n.t('common.dauyan_account_login_wechat')}</div>
            </div>
          )
        }
        {isWechatLogin ? (
          <WechatLogin
            scanQrcode={scanQrcode}
            isLong={isLong}
            setIsLong={setIsLong}
            tosAgreed={tosAgreed}
            updateToken={updateToken} />
        ) : (
          <NormalLogin
            wxCode={wxId}
            wxTicket={wxTicket}
            isLong={isLong}
            setIsLong={setIsLong}
            setTosAgreed={settOsAgreed}
            loginSuccess={loginSuccess}
            tosAgreed={tosAgreed}
            notDevInWhiteList={notDevInWhiteList}
            setTosAgreedVisible={setTosAgreedVisible} />
        )}
        {
          tosAgreedVisible && <div className="protocol-container">
            <input
              type="checkbox"
              className="checkspan"
              disabled={isWechatLogin}
              checked={isWechatLogin ? true : tosAgreed}
              onChange={e => settOsAgreed(e.target.checked)}
              placeholder={i18n.t('common.dauyan_account_term')}
            />

            <span className="protocol-content">
              {i18n.t('common.dauyan_account_term_login_agree')}
              {' '}
              <span className="protocol-mark">
                <a href={links.userAgreement} target="_blank" rel="noreferrer">{i18n.t('common.dauyan_account_term_user')}</a>
              </span>
              {i18n.t('common.dauyan_account_term_comma')}
              <span className="protocol-mark">
                <a href={links.privacyPolicy} target="_blank" rel="noreferrer">{i18n.t('common.dauyan_account_term_privacy')}</a>
              </span>
              {i18n.t('common.dauyan_account_term_comma')}
              <span className="protocol-mark">
                <a href={links.disclaimer} target="_blank" rel="noreferrer">{i18n.t('common.dauyan_account_term_disclaimer')}</a>
              </span>
              {i18n.t('common.dauyan_account_term_comma')}
              <span className="protocol-mark">
                <a href={links.copyright} target="_blank" rel="noreferrer">{i18n.t('common.dauyan_account_term_copyright')}</a>
              </span>
            </span>
          </div>
        }

      </div>
      <Toaster />
    </div>
  );
};

export default Login;
